import api from "@/lib/api";

// Chunked, storage-first upload: init → PUT parts (idempotent, per-part retry) → complete (idempotent). Resumable via session.missing.
export const uploadErrorMessage = (e, limits) => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (status === 413) return `File too large for the upload gateway${limits?.max_mb ? ` (application limit ${limits.max_mb} MB)` : ""}.`;
  if (status === 502 || status === 503 || status === 504) return `Server temporarily unavailable or restarting (${status}).`;
  if (status === 401 || status === 403) return "You are not allowed to upload to this project.";
  if (!e?.response) return "Connection lost during upload.";
  return `Server error (${status}).`;
};

async function sha256Hex(file) {
  if (!window.crypto?.subtle || file.size > 256 * 1024 * 1024) return null; // hashing very large files in-browser is slow; server still verifies size/parts
  const buf = await file.arrayBuffer();
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", buf))].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function putPart(sid, n, blob, attempt = 0) {
  try {
    await api.put(`/uploads/${sid}/parts/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" }, timeout: 0 });
  } catch (e) {
    const status = e?.response?.status;
    if (attempt < 4 && (!e.response || [502, 503, 504, 500].includes(status))) {
      await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      return putPart(sid, n, blob, attempt + 1);
    }
    throw e;
  }
}

// onState({phase, loaded, total, sid, ...}). Returns the created document. `session` lets a failed upload resume (only missing parts are re-sent).
export async function chunkedUpload({ file, projectId, form, limits, onState, session }) {
  const total = file.size;
  let s = session;
  if (!s) {
    const sha256 = await sha256Hex(file);
    const { data } = await api.post(`/uploads/init`, { project_id: projectId, filename: file.name, size: total, name: form.name || file.name, doc_type: form.doc_type, csi_division: form.csi_division, sha256 });
    s = data;
  } else {
    const { data } = await api.get(`/uploads/${s.id}`);
    s = data;
  }
  const received = new Set(s.received || []);
  const t0 = Date.now();
  let loaded = received.size * s.part_size;
  onState({ phase: "transfer", sid: s.id, loaded, total, elapsed: 0, rate: 0, session: s });
  for (let n = 1; n <= s.parts_total; n++) {
    if (received.has(n)) continue;
    const start = (n - 1) * s.part_size, end = Math.min(total, start + s.part_size);
    await putPart(s.id, n, file.slice(start, end));
    received.add(n);
    loaded = Math.min(total, loaded + (end - start));
    const el = (Date.now() - t0) / 1000;
    onState({ phase: "transfer", sid: s.id, loaded, total, elapsed: el, rate: el > 0 ? loaded / el : 0, session: { ...s, received: [...received] } });
  }
  onState({ phase: "finalizing", sid: s.id, loaded: total, total, session: s });
  const { data: doc } = await api.post(`/uploads/${s.id}/complete`, null, { timeout: 0 });
  return doc;
}

export async function uploadStatus(sid) {
  const { data } = await api.get(`/uploads/${sid}`);
  return data;
}
